/**
 * A store tile's offer count: `"3 Coupons & 6 Deals"` — but only the halves that actually exist.
 *
 * Shared rather than copied. The home page's partnered stores grid, a category's top stores and an
 * event's store tiles all print this same line for the same stores, so it should read identically
 * wherever a reader meets it.
 *
 * The rule the old template literal got wrong: a store with deals but no codes — much the commoner
 * case — rendered as `"0 Coupons & 9 Deals"`. Opening on a zero reads as a gap rather than as nine
 * offers, and the empty half tells the reader nothing at all. Dropping it leaves `"9 Deals"`, which
 * is both true and useful. The same applies in reverse for a store carrying only codes.
 *
 * Singulars are spelled properly while we are here: one code is `"1 Coupon"`, not `"1 Coupons"`.
 *
 * With nothing on either side the result is an empty string, so a caller can drop the line entirely
 * rather than print a zero.
 */
export function formatOfferCount(couponsCount: number, dealsCount: number): string {
  const parts: string[] = [];

  const add = (raw: number, noun: string): void => {
    const count = Number.isFinite(raw) ? Math.max(0, Math.trunc(raw)) : 0;
    if (count > 0) {
      parts.push(`${count} ${noun}${count === 1 ? '' : 's'}`);
    }
  };

  add(couponsCount, 'Coupon');
  add(dealsCount, 'Deal');

  return parts.join(' & ');
}
